import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { Link as RouterLink } from 'react-router-dom';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { FinanceTodoItemDto } from '../../../../common/interfaces/invoicing.interface';
import { BillingSetupSubTab } from '../../../../common/enums/common.enum';
import {
  FINANCE_CONTRACTS,
  FINANCE_DEBTS,
  FINANCE_INVOICES,
  FINANCE_SETTINGS,
  INVOICE_CREATE
} from '../../../../common/constants/routes.constants';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { formatSerbianDate } from '../../../../utilities/helper/date.helper';
import { MISSING_LABELS } from '../Billing/settingsLabels';
import { forMonth, monthKeyOf } from './monthText';
import { groupQuery, KINDERGARTEN_SCOPE, OverviewScope, placeText } from './scope';

const TONE = {
  action: { dot: 'primary.main', bg: 'surface.tint' },
  error: { dot: 'error.main', bg: 'tint.error' },
  warning: { dot: 'warning.main', bg: 'tint.warning' }
} as const;

/** The invoice list's state filter each invoice line opens on. */
const INVOICE_STATE: Partial<Record<FinanceTodoItemDto['key'], string>> = {
  SEND_FAILED: 'sendFailed',
  DRAFTS_TO_ISSUE: 'draft',
  ISSUED_NOT_SENT: 'issuedNotSent'
};

export const todoHref = (item: FinanceTodoItemDto, kindergartenId: string, scope: OverviewScope = KINDERGARTEN_SCOPE): string => {
  const kg = `kindergartenId=${kindergartenId}`;
  const groups = groupQuery(scope);
  const month = item.year && item.month ? `&month=${monthKeyOf(item.year, item.month)}` : '';
  switch (item.targetKey) {
    case 'invoices': {
      const state = INVOICE_STATE[item.key] ? `&state=${INVOICE_STATE[item.key]}` : '';
      return `${FINANCE_INVOICES}?${kg}${month}${state}${groups}`;
    }
    case 'newInvoice':
      return `${INVOICE_CREATE}?${kg}&childId=${item.names[0]?.childId ?? ''}${month}`;
    case 'debts':
      return `${FINANCE_DEBTS}?${kg}&bucket=late30${groups}`;
    case 'contracts':
      return `${FINANCE_CONTRACTS}?${kg}${groups}`;
    case 'schemes':
      return `${FINANCE_SETTINGS}?${kg}&tab=${BillingSetupSubTab.Schemes}`;
    default:
      return `${FINANCE_SETTINGS}?${kg}&tab=${BillingSetupSubTab.Settings}`;
  }
};

/** The line's two sentences and its button, from the server's facts. */
export const todoText = (
  t: TFunction,
  item: FinanceTodoItemDto,
  scope: OverviewScope = KINDERGARTEN_SCOPE
): { title: string; detail: string; action: string } => {
  const text = lineText(t, item, scope);
  const place = placeText(t, scope);
  // the place is said once, after what the line is about; a child's line already names its group
  return place && item.key !== 'NO_INVOICE_FOR_MONTH' && item.key !== 'GROUP_WITHOUT_SCHEME'
    ? { ...text, title: `${text.title} ${place}` }
    : text;
};

const lineText = (t: TFunction, item: FinanceTodoItemDto, scope: OverviewScope): { title: string; detail: string; action: string } => {
  const month = item.year && item.month ? forMonth(t, monthKeyOf(item.year, item.month)) : '';
  const base = `finance.todo.${item.key}`;
  switch (item.key) {
    case 'NO_INVOICE_FOR_MONTH':
      return {
        title: t(`${base}.title`, { name: item.names[0]?.name ?? '', month }),
        detail: [item.date ? t(`${base}.joined`, { date: formatSerbianDate(item.date) }) : null, t(`${base}.detail`, { month })]
          .filter(Boolean)
          .join(' · '),
        action: t(`${base}.action`)
      };
    case 'ISSUED_NOT_SENT':
      return {
        title: t(`${base}.title`, { count: item.count, month }),
        detail: [
          item.date ? t(`${base}.issued`, { date: formatSerbianDate(item.date) }) : null,
          t(`${base}.withEmail`, { count: item.withEmail ?? 0 })
        ]
          .filter(Boolean)
          .join(' · '),
        action: t(`${base}.action`)
      };
    case 'OVERDUE_30':
      return {
        title: t(`${base}.title`, { count: item.count }),
        // a group is a handful of children: who, rather than how much in all
        detail: (scope.kind === 'group'
          ? [
              item.names
                .slice(0, 2)
                .map(entry => entry.name)
                .join(', '),
              t(`${base}.longestDays`, { count: item.days ?? 0 })
            ]
          : [
              t(`${base}.total`, { amount: formatMoney(item.amount ?? 0) }),
              item.names[0] ? t(`${base}.longest`, { name: item.names[0].name, count: item.days ?? 0 }) : null
            ]
        )
          .filter(Boolean)
          .join(' · '),
        action: t(`${base}.action`)
      };
    case 'CONTRACTS_NOT_READY':
      return {
        title: t(`${base}.title`, { count: item.count, month }),
        detail: item.names.map(entry => `${entry.name}: ${t(`finance.todo.reasons.${entry.reason ?? 'price'}`)}`).join(' · '),
        action: t(`${base}.action`)
      };
    case 'GROUP_WITHOUT_SCHEME':
      return {
        title: item.count === 1 ? t(`${base}.one`, { name: item.names[0]?.name ?? '' }) : t(`${base}.many`, { count: item.count }),
        detail: item.count === 1 ? t(`${base}.detail`) : `${item.names.map(entry => entry.name).join(', ')} · ${t(`${base}.detail`)}`,
        action: t(`${base}.action`)
      };
    case 'SETTINGS_INCOMPLETE':
      return {
        title: t(`${base}.title`),
        detail: t(`${base}.detail`, { what: item.missing.map(key => (MISSING_LABELS[key] ? t(MISSING_LABELS[key]) : key)).join(', ') }),
        action: t(`${base}.action`)
      };
    default:
      // SEND_FAILED, DRAFTS_TO_ISSUE
      return {
        title: t(`${base}.title`, { count: item.count, month }),
        detail: item.amount !== null ? t(`${base}.detail`, { amount: formatMoney(item.amount), month }) : t(`${base}.detail`, { month }),
        action: t(`${base}.action`)
      };
  }
};

const MAX_ROWS = 5;

/**
 * What needs a hand today, most urgent first - the server's order - and at most five, each
 * a link to the one screen that deals with it. It is about today, whatever month the
 * figures below are showing.
 */
const TodoList: React.FC<{
  items: FinanceTodoItemDto[];
  kindergartenId: string;
  scope?: OverviewScope;
  /** A group's page opens its price list in place rather than going to Settings. */
  onOpenScheme?: () => void;
}> = ({ items, kindergartenId, scope = KINDERGARTEN_SCOPE, onOpenScheme }) => {
  const { t } = useTranslation();
  const shown = items.slice(0, MAX_ROWS);
  return (
    <Box component="section" aria-labelledby="finance-todo-title" sx={{ mb: 4 }}>
      <Typography id="finance-todo-title" component="h2" sx={{ fontSize: 16, fontWeight: 600, mb: 1.5 }}>
        {t('finance.todo.heading')}
      </Typography>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, border: 1, borderColor: 'line', borderRadius: 4 }}>
        {!shown.length ? (
          <Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '14px 20px' }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'tint.info',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CheckIcon aria-hidden sx={{ fontSize: 18, color: 'success.main' }} />
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{t('finance.todo.allDone')}</Typography>
          </Box>
        ) : (
          shown.map((item, index) => {
            const text = todoText(t, item, scope);
            const inPlace = item.targetKey === 'schemes' && onOpenScheme;
            const tone = TONE[item.tone];
            return (
              <Box
                component="li"
                key={`${item.key}-${index}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '14px 20px', '& + &': { borderTop: 1, borderColor: 'line' } }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    borderRadius: '50%',
                    bgcolor: tone.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: tone.dot }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{text.title}</Typography>
                  {text.detail && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{text.detail}</Typography>}
                </Box>
                <Link
                  {...(inPlace
                    ? { component: 'button' as const, type: 'button' as const, onClick: onOpenScheme }
                    : { component: RouterLink, to: todoHref(item, kindergartenId, scope) })}
                  underline="hover"
                  aria-label={`${text.action}: ${text.title}`}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: 14,
                    fontWeight: 500,
                    color: 'primary.main',
                    flexShrink: 0
                  }}
                >
                  {text.action}
                  <ArrowForwardIcon aria-hidden sx={{ fontSize: 16 }} />
                </Link>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default TodoList;
